/**
 * Unit Location Map Component
 * Displays a focused site plan showing only the highlighted unit location
 * All other units are shown as faded/de-emphasized
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { MapPin } from 'lucide-react';
import { getSiteMapData, type SiteMapPolygon } from '@/lib/site-map-data';
import { getSiteMapConfig, DEFAULT_SITE_MAP_VIEWBOX, type SiteMapConfig } from '@/lib/site-config';
import type { PropertyUnit } from '@/lib/units';
import { cn } from '@/lib/utils';
import { CompassRose } from './CompassRose';

/** Default viewBox dimensions for the site map */
const DEFAULT_VIEWBOX_WIDTH = 1728;
const DEFAULT_VIEWBOX_HEIGHT = 864;

// Cache for site map config to avoid repeated database calls
let cachedConfig: SiteMapConfig | null = null;
let configLoadPromise: Promise<SiteMapConfig> | null = null;

async function loadCachedConfig(): Promise<SiteMapConfig> {
  if (cachedConfig) return cachedConfig;
  if (configLoadPromise) return configLoadPromise;
  
  configLoadPromise = getSiteMapConfig().then(config => {
    cachedConfig = config;
    return config;
  });
  
  return configLoadPromise;
}

// Export function to invalidate cache (called after admin saves)
export function invalidateSiteMapConfigCache() {
  cachedConfig = null;
  configLoadPromise = null;
}

interface UnitLocationMapProps {
  /** The unit number to highlight */
  unitNumber: string;
  /** Show compact version for modals */
  compact?: boolean;
  /** Show "Unit Location" header */
  showTitle?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Custom viewBox for the SVG (default: "0 0 1500 864" - centered on units) */
  viewBox?: string;
  /** Optional background image URL for overlays (e.g., parking, shopping areas) */
  backgroundImage?: string;
  /** Horizontal offset to shift view in pixels (positive = shift right, negative = shift left) */
  centerOffset?: number;
  /** Zoom level for compact mode (1 = normal, 2 = 2x zoom, etc). Default: 1.8 */
  compactZoom?: number;
}

/**
 * Helper to convert block letter to user-friendly view name
 */
function getBlockDisplayName(block: 'A' | 'B'): string {
  return block === 'A' ? 'Canal View' : 'Lagoon View';
}

export function UnitLocationMap({
  unitNumber,
  compact = false,
  showTitle = true,
  className,
  viewBox,
  backgroundImage,
  centerOffset,
  compactZoom = 1.15,
}: UnitLocationMapProps) {
  const [siteMapPolygons, setSiteMapPolygons] = useState<SiteMapPolygon[]>([]);
  const [polygonUnitsMap, setPolygonUnitsMap] = useState<Map<string, PropertyUnit[]>>(new Map());
  const [highlightedPolygonId, setHighlightedPolygonId] = useState<string | null>(null);
  const [highlightedUnit, setHighlightedUnit] = useState<PropertyUnit | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dbConfig, setDbConfig] = useState<SiteMapConfig | null>(null);

  // Load database config if props are not provided
  const propsProvided = viewBox !== undefined || centerOffset !== undefined || backgroundImage !== undefined;
  
  useEffect(() => {
    if (!propsProvided) {
      loadCachedConfig().then(setDbConfig);
    }
  }, [propsProvided]);

  // Compute the final values: props take precedence over database config
  const finalCenterOffset = centerOffset ?? dbConfig?.centerOffset;
  const finalViewBox = viewBox ?? dbConfig?.viewBox ?? DEFAULT_SITE_MAP_VIEWBOX;
  const finalBackgroundImage = backgroundImage ?? dbConfig?.backgroundImage;
  
  // Compute the base viewBox string
  const baseViewBox = finalCenterOffset !== undefined
    ? `${finalCenterOffset} 0 ${DEFAULT_VIEWBOX_WIDTH - Math.abs(finalCenterOffset)} ${DEFAULT_VIEWBOX_HEIGHT}`
    : finalViewBox;
  
  // Apply zoom for compact mode - zoom into center of viewBox
  const computedViewBox = (() => {
    if (!compact || compactZoom <= 1) return baseViewBox;
    
    const [x, y, w, h] = baseViewBox.split(' ').map(Number);
    const newW = w / compactZoom;
    const newH = h / compactZoom;
    const newX = x + (w - newW) / 2;
    const newY = y + (h - newH) / 2;
    
    return `${newX} ${newY} ${newW} ${newH}`;
  })();

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Single optimized query with caching
      const { data, error: fetchError } = await getSiteMapData();
      
      if (fetchError || !data) {
        setError('Failed to load site map');
        setIsLoading(false);
        return;
      }
      
      setSiteMapPolygons(data.polygons);
      setPolygonUnitsMap(data.polygonUnitsMap);
      
      // Find the target unit
      const targetUnit = data.units.find(u => u.unit_number === unitNumber);
      if (targetUnit) {
        setHighlightedUnit(targetUnit);
      }
      
      // Find which polygon contains the target unit
      let foundPolygonId: string | null = null;
      for (const [polygonId, units] of data.polygonUnitsMap) {
        if (units.some(u => u.unit_number === unitNumber)) {
          foundPolygonId = polygonId;
          break;
        }
      }
      setHighlightedPolygonId(foundPolygonId);
      
    } catch (err) {
      console.error('Error loading unit location map:', err);
      setError('Failed to load map data');
    } finally {
      setIsLoading(false);
    }
  }, [unitNumber]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  function renderPolygon(polygon: SiteMapPolygon) {
    const coords = polygon.map_coordinates as { type?: string; points?: number[][] } | null;
    
    if (!coords || coords.type !== 'Polygon' || !coords.points) {
      return null;
    }

    const isHighlighted = polygon.id === highlightedPolygonId;
    const unitsAtPolygon = polygonUnitsMap.get(polygon.id) || [];
    const hasUnits = unitsAtPolygon.length > 0;

    // Styling based on highlight state
    let fillColor: string;
    let fillOpacity: number;
    let strokeColor: string;
    let strokeWidth: number;
    let strokeOpacity: number;

    if (isHighlighted) {
      // Highlighted unit - brand teal
      fillColor = '#0d9488'; // brand-teal
      fillOpacity = 0.8;
      strokeColor = '#0f766e'; // darker teal
      strokeWidth = 3;
      strokeOpacity = 1;
    } else {
      // All other polygons - light gray fill
      fillColor = '#cbd5e1';
      fillOpacity = hasUnits ? 0.6 : 0.3;
      strokeColor = '#94a3b8';
      strokeWidth = 0.5;
      strokeOpacity = hasUnits ? 0.8 : 0.4;
    }

    const pointsString = coords.points
      .map((point: number[]) => `${point[0]},${point[1]}`)
      .join(' ');

    return (
      <polygon
        key={polygon.id}
        points={pointsString}
        fill={fillColor}
        fillOpacity={fillOpacity}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        strokeOpacity={strokeOpacity}
        className="transition-all duration-200"
      />
    );
  }

  // Determine which block the unit is in for display
  const unitBlock = highlightedUnit?.block || 
    (highlightedPolygonId 
      ? siteMapPolygons.find(p => p.id === highlightedPolygonId)?.block 
      : null);

  if (isLoading) {
    return (
      <div className={cn(
        "flex items-center justify-center h-32",
        className
      )}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary mx-auto mb-1"></div>
          <p className="text-xs text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn(
        "flex items-center justify-center h-32",
        className
      )}>
        <p className="text-xs text-muted-foreground">{error}</p>
      </div>
    );
  }

  return (
    <div className={cn(
      "w-full overflow-hidden",
      compact ? "h-auto" : "h-full",
      showTitle && "space-y-1",
      className
    )}>
      {showTitle && (
        <div className="flex items-center justify-center gap-2 text-xs font-medium text-muted-foreground">
          <MapPin className="w-3 h-3" />
          <span>Unit Location</span>
          {unitBlock && (
            <span className="text-[10px] bg-muted px-1.5 py-0.5 rounded">
              {getBlockDisplayName(unitBlock)}
            </span>
          )}
        </div>
      )}
      
      <div className={cn(
        "relative w-full flex items-center justify-center overflow-hidden",
        compact ? "h-32" : "h-full",
        showTitle && !compact && "h-32"
      )}>
        <svg
          viewBox={computedViewBox}
          className="w-full h-full max-w-full max-h-full"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="xMidYMid meet"
        >
          {/* Optional background image for overlays (parking, shopping, Grand Harbour view) */}
          {finalBackgroundImage && (
            <image
              href={finalBackgroundImage}
              x="0"
              y="0"
              width={DEFAULT_VIEWBOX_WIDTH}
              height={DEFAULT_VIEWBOX_HEIGHT}
              preserveAspectRatio="xMidYMid slice"
            />
          )}
          
          {/* Render all polygons (faded except highlighted) */}
          {siteMapPolygons.map(polygon => renderPolygon(polygon))}
        </svg>

        {/* Compass Rose overlay */}
        <CompassRose
          size={compact ? 28 : 36}
          northOffsetDegrees={dbConfig?.northOffsetDegrees ?? 0}
          className="absolute bottom-1 right-1"
        />
      </div>
    </div>
  );
}
